/**
 * Plugin-side bridge client for the host ↔ plugin platform contract
 * (WI 4858 sub-plan 2).
 *
 * `createPortBridgeClient` wraps a MessagePort (or an in-realm shim) and
 * exposes typed subscription callbacks for each host push (theme, locale,
 * density, a11y, nav, session token) and typed fire-and-wait calls for
 * chrome requests and a11y live-region announcements.
 *
 * For in-realm (Tier T) plugins, use `InMemoryBridgeTransport` from the
 * mock-host package instead — it calls callbacks synchronously without
 * any serialisation.
 */
import {
  BRIDGE_CHROME_REQUEST,
  BRIDGE_A11Y_ANNOUNCE,
  isBridgePushTheme,
  isBridgePushLocale,
  isBridgePushDensity,
  isBridgePushA11y,
  isBridgeNavPush,
  isBridgeChromeResponse,
  isBridgeSessionTokenPush,
  type BridgePushThemeEnvelope,
  type BridgePushLocaleEnvelope,
  type BridgePushDensityEnvelope,
  type BridgePushA11yEnvelope,
  type BridgeNavPushEnvelope,
  type BridgeSessionTokenPushEnvelope,
} from "../host/worker/bridge-envelopes";

// ── Subscriber payload types (strips the `type` discriminant) ────────────────

export type ThemePayload  = Omit<BridgePushThemeEnvelope, "type">;
export type LocalePayload = Omit<BridgePushLocaleEnvelope, "type">;
export type DensityPayload = Omit<BridgePushDensityEnvelope, "type">;
export type A11yPayload   = Omit<BridgePushA11yEnvelope, "type">;
export type NavPayload    = Omit<BridgeNavPushEnvelope, "type">;
export type SessionTokenPayload = Omit<BridgeSessionTokenPushEnvelope, "type">;

// ── Port shim (test injection) ───────────────────────────────────────────────

/**
 * Minimal port surface `createPortBridgeClient` actually uses. Matches
 * `PortShim` in `createPortMcpTransport` — same test-injection pattern.
 */
export interface BridgePortShim {
  onmessage: ((ev: { data: unknown }) => void) | null;
  postMessage(msg: unknown): void;
}

// ── Public client interface ──────────────────────────────────────────────────

export interface PortBridgeClient {
  /** Replace the theme subscriber. Only the latest subscriber is called. */
  onTheme(cb: (payload: ThemePayload) => void): void;
  /** Replace the locale subscriber. */
  onLocale(cb: (payload: LocalePayload) => void): void;
  /** Replace the density subscriber. */
  onDensity(cb: (payload: DensityPayload) => void): void;
  /** Replace the a11y-preferences subscriber. */
  onA11y(cb: (payload: A11yPayload) => void): void;
  /** Replace the nav-state subscriber. */
  onNav(cb: (payload: NavPayload) => void): void;
  /** Replace the session-token subscriber. */
  onSessionToken(cb: (payload: SessionTokenPayload) => void): void;
  /**
   * Request a host chrome action. Returns the host's result or rejects with
   * the host's error string. Callers should catch and surface gracefully.
   *
   * @param action "toast" | "confirm" | "openUrl"
   * @param payload Action-specific payload.
   */
  requestChrome(action: string, payload: Record<string, unknown>): Promise<unknown>;
  /**
   * Ask the host to make a live-region announcement on behalf of the plugin.
   * Prefers this over writing directly to the DOM so the host can manage
   * ARIA live regions centrally.
   */
  announceA11y(message: string, politeness: "polite" | "assertive"): void;
}

// ── Implementation ───────────────────────────────────────────────────────────

let _requestCounter = 0;
function nextId(): string {
  _requestCounter += 1;
  return `br-${_requestCounter}`;
}

/**
 * Construct a bridge client backed by a `MessagePort` (or shim).
 *
 * Registers a single `onmessage` handler on the port that dispatches each
 * bridge push to the appropriate subscriber. Unknown message types are
 * silently ignored — the port carries MCP traffic on the same channel and
 * those messages must not trigger an error here.
 */
export function createPortBridgeClient(port: BridgePortShim): PortBridgeClient {
  let themeCb:  ((p: ThemePayload) => void)  | undefined;
  let localeCb: ((p: LocalePayload) => void) | undefined;
  let densityCb: ((p: DensityPayload) => void) | undefined;
  let a11yCb:   ((p: A11yPayload) => void)   | undefined;
  let navCb:    ((p: NavPayload) => void)    | undefined;
  let tokenCb:  ((p: SessionTokenPayload) => void) | undefined;

  type PendingChrome = { resolve: (v: unknown) => void; reject: (e: Error) => void };
  const pending = new Map<string, PendingChrome>();

  port.onmessage = (ev: { data: unknown }): void => {
    const msg = ev.data;
    if (isBridgePushTheme(msg))        { themeCb?.({ mode: msg.mode, tokens: msg.tokens }); return; }
    if (isBridgePushLocale(msg))       { localeCb?.({ locale: msg.locale, dir: msg.dir }); return; }
    if (isBridgePushDensity(msg))      { densityCb?.({ density: msg.density }); return; }
    if (isBridgePushA11y(msg))         { a11yCb?.({ reducedMotion: msg.reducedMotion, highContrast: msg.highContrast }); return; }
    if (isBridgeNavPush(msg))          { navCb?.({ path: msg.path, historyLength: msg.historyLength }); return; }
    if (isBridgeSessionTokenPush(msg)) { tokenCb?.({ token: msg.token, expiresAtMs: msg.expiresAtMs }); return; }
    if (isBridgeChromeResponse(msg)) {
      const p = pending.get(msg.id);
      if (p === undefined) { return; }
      pending.delete(msg.id);
      if (msg.ok) { p.resolve(msg.result ?? null); }
      else { p.reject(new Error(typeof msg.error === "string" ? msg.error : "Chrome request failed")); }
    }
  };

  return {
    onTheme(cb) { themeCb = cb; },
    onLocale(cb) { localeCb = cb; },
    onDensity(cb) { densityCb = cb; },
    onA11y(cb) { a11yCb = cb; },
    onNav(cb) { navCb = cb; },
    onSessionToken(cb) { tokenCb = cb; },

    requestChrome(action, payload): Promise<unknown> {
      const id = nextId();
      return new Promise<unknown>((resolve, reject) => {
        pending.set(id, { resolve, reject });
        port.postMessage({ type: BRIDGE_CHROME_REQUEST, id, action, payload });
      });
    },

    announceA11y(message, politeness): void {
      port.postMessage({ type: BRIDGE_A11Y_ANNOUNCE, message, politeness });
    },
  };
}
